import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const SENSORY = '#2493A2';
const SYMBOLIC = '#DCB13C';

interface MemorySpike {
  position: number; // 0-1 along timeline (0 = far past, 1 = now)
  height: number;
  label: string;
  color: string;
}

export function MemorySpikeTimeline() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const height = 140;
    const margin = { left: 20, right: 20, top: 20, bottom: 30 };
    const lineY = height - margin.bottom - 20;
    const lineStart = margin.left;
    const lineEnd = width - margin.right;
    const lineWidth = lineEnd - lineStart;

    // --- Timeline base line ---
    svg
      .append('line')
      .attr('x1', lineStart)
      .attr('y1', lineY)
      .attr('x2', lineEnd)
      .attr('y2', lineY)
      .attr('stroke', 'var(--color-text-muted)')
      .attr('stroke-width', 1.5)
      .attr('opacity', 0.4);

    // --- "now" marker ---
    const nowX = lineEnd;
    svg
      .append('circle')
      .attr('cx', nowX)
      .attr('cy', lineY)
      .attr('r', 4)
      .attr('fill', SENSORY);

    svg
      .append('text')
      .attr('x', nowX)
      .attr('y', lineY + 16)
      .attr('text-anchor', 'middle')
      .attr('font-size', 9)
      .attr('font-weight', 600)
      .attr('class', 'viz-text-muted')
      .text('now');

    // --- Past label ---
    svg
      .append('text')
      .attr('x', lineStart)
      .attr('y', lineY + 16)
      .attr('text-anchor', 'start')
      .attr('font-size', 9)
      .attr('class', 'viz-text-muted')
      .text('past');

    // --- Memory spikes ---
    const spikes: MemorySpike[] = [
      { position: 0.08, height: 50, label: 'home', color: SYMBOLIC },
      { position: 0.25, height: 38, label: 'danger', color: '#E85D75' },
      { position: 0.42, height: 55, label: 'comfort', color: SENSORY },
      { position: 0.6, height: 30, label: 'summer', color: SYMBOLIC },
      { position: 0.78, height: 44, label: 'loss', color: '#E85D75' },
    ];

    const spikeGroup = svg.append('g').attr('class', 'spikes');

    spikes.forEach((spike, i) => {
      const x = lineStart + spike.position * lineWidth;

      // Spike line
      spikeGroup
        .append('line')
        .attr('x1', x)
        .attr('y1', lineY)
        .attr('x2', x)
        .attr('y2', lineY)
        .attr('stroke', spike.color)
        .attr('stroke-width', 2)
        .attr('opacity', 0)
        .transition()
        .delay(400 + i * 300)
        .duration(350)
        .attr('y2', lineY - spike.height)
        .attr('opacity', 0.85);

      // Spike dot at peak
      spikeGroup
        .append('circle')
        .attr('cx', x)
        .attr('cy', lineY)
        .attr('r', 3)
        .attr('fill', spike.color)
        .attr('opacity', 0)
        .transition()
        .delay(400 + i * 300)
        .duration(350)
        .attr('cy', lineY - spike.height)
        .attr('opacity', 1);

      // Spike label
      spikeGroup
        .append('text')
        .attr('x', x)
        .attr('y', lineY - spike.height - 8)
        .attr('text-anchor', 'middle')
        .attr('font-size', 9)
        .attr('font-weight', 600)
        .attr('class', 'viz-text')
        .attr('opacity', 0)
        .text(spike.label)
        .transition()
        .delay(600 + i * 300)
        .duration(300)
        .attr('opacity', 1);

      // Faint glow circle
      spikeGroup
        .append('circle')
        .attr('cx', x)
        .attr('cy', lineY - spike.height)
        .attr('r', 0)
        .attr('fill', spike.color)
        .attr('opacity', 0)
        .transition()
        .delay(500 + i * 300)
        .duration(400)
        .attr('r', 10)
        .attr('opacity', 0.12);
    });

    // --- Connecting flash from "now" to spikes ---
    const tFlash = d3.timeout(() => {
      spikes.forEach((spike) => {
        const x = lineStart + spike.position * lineWidth;
        svg
          .append('line')
          .attr('x1', nowX)
          .attr('y1', lineY)
          .attr('x2', nowX)
          .attr('y2', lineY)
          .attr('stroke', SENSORY)
          .attr('stroke-width', 1)
          .attr('opacity', 0.5)
          .attr('stroke-dasharray', '3 3')
          .transition()
          .duration(400)
          .attr('x2', x)
          .attr('y2', lineY - spike.height)
          .transition()
          .duration(300)
          .attr('opacity', 0)
          .remove();
      });
    }, 2200);

    return () => {
      tFlash.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-wind" /> Smell &mdash; Memory Collapse
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 140" />
      <p className="viz-container__caption">Smell collapses time.</p>
    </div>
  );
}
